/**
 * Cart checkout math for TicketCheckout.vue (/web/checkout and the mobile cart webview).
 * The backend trusts these numbers (SPO-1241, SPO-1259): they are what the customer is charged.
 */
import type { CartLine } from '@/store/modules/cart';
import { calculateCartFees } from '@/utils/priceWithFees';

export { calculateCartFees };

/** Price of one unit as shown in the cart's PRICE column */
export function getLineUnitPrice(line: CartLine): number {
  if (line.lineType === 'Donation') {
    return line.donationAmount ?? 0;
  }
  if (line.sweepstakesEntryCost != null) {
    return line.sweepstakesEntryCost;
  }
  return line.unitCost ?? 0;
}

/** Line total before fees; Donation and Sticker lines are charged once per selected cause */
export function calculateLineTotal(line: CartLine): number {
  if (line.isFree) return 0;
  if (line.lineType === 'Ticket') {
    return (line.unitCost ?? 0) * (line.quantity ?? 1);
  }
  if (line.lineType === 'Donation') {
    const amount = line.donationAmount ?? 0;
    const causeCount = line.donationCauseIds?.length ?? 1;
    return amount * causeCount;
  }
  if (line.lineType === 'Sticker') {
    const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
    const causeCount = line.donationCauseIds?.length ?? 1;
    return amount * causeCount;
  }
  return line.unitCost ?? 0;
}

export function calculateCartSubtotal(lines: CartLine[]): number {
  return lines.reduce((sum, line) => sum + calculateLineTotal(line), 0);
}

/** Subtotal + platform fee + card fee, rounded to cents; 0 for an empty or free cart */
export function cartTotalWithFees(
  subtotal: number,
  fees: { convenienceFee: number; creditCardFee: number },
): number {
  if (subtotal === 0) return 0;
  return parseFloat((subtotal + fees.convenienceFee + fees.creditCardFee).toFixed(2));
}

/**
 * The part of the cart the Giving Wallet may pay for: donations, campaign tickets (ticket lines
 * with a cause) and Campaign stickers. MatchCampaign and other sticker types are not eligible.
 */
export function walletEligibleTotal(lines: CartLine[]): number {
  let total = 0;

  for (const line of lines) {
    // Donation add-ons are always wallet-eligible
    if (line.lineType === 'Donation') {
      const amount = line.donationAmount ?? 0;
      const causeCount = line.donationCauseIds?.length ?? 1;
      total += amount * causeCount;
      continue;
    }

    // Campaign tickets (ticket lines with donationCauseId) are wallet-eligible
    if (line.lineType === 'Ticket' && line.donationCauseId) {
      const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
      total += amount;
      continue;
    }

    // Sticker lines: Campaign stickers are wallet-eligible, MatchCampaign are NOT
    if (line.lineType === 'Sticker') {
      const campaignType = line.campaignType?.toLowerCase();
      if (campaignType === 'campaign') {
        const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
        const causeCount = line.donationCauseIds?.length ?? 1;
        total += amount * causeCount;
      }
      continue;
    }
  }

  return total;
}

/** The most wallet credit the cart can use: the balance, up to the eligible part */
export function maxWalletCredit(walletBalance: number, lines: CartLine[]): number {
  return Math.min(walletBalance, walletEligibleTotal(lines));
}

/** Wallet credit applied: all of maxWalletCredit when the buyer ticks "Use Giving Wallet" */
export function givingWalletAmount(useGivingWallet: boolean, maxCredit: number): number {
  return useGivingWallet ? maxCredit : 0;
}

/** What is left to pay by card after the wallet credit */
export function chargeAfterWallet(totalWithFees: number, walletAmount: number): number {
  return Math.max(0, totalWithFees - walletAmount);
}

/**
 * The Total row: once the payment form is mounted, the amount the server put on the
 * PaymentIntent (cents); before that, the cart total with fees.
 */
export function checkoutTotalAmount(
  paymentMounted: boolean,
  serverAmountCents: number | null,
  totalWithFees: number,
): number {
  if (paymentMounted && serverAmountCents !== null) {
    return serverAmountCents / 100;
  }
  return totalWithFees;
}

/** The `lines` input of createCartCheckoutIntent */
export function buildCheckoutLines(lines: CartLine[]) {
  return lines.map((line) => {
    const out: any = {
      stickerId: line.stickerId,
      lineType: line.lineType,
    };
    if (line.variantId) out.variantId = line.variantId;
    if (line.quantity) out.quantity = line.quantity;
    if (line.selectedOption) out.selectedOption = line.selectedOption;
    if (line.addOnId) out.addOnId = line.addOnId;

    if (line.sweepstakesEntries) out.sweepstakesEntries = line.sweepstakesEntries;

    // Multi-cause support: donationCauseIds takes precedence
    if (line.donationCauseIds && line.donationCauseIds.length > 0) {
      out.donationCauseIds = line.donationCauseIds;

      // For Sticker lines with multiple causes, backend needs donationAmount
      // This is the per-cause amount that backend will multiply by cause count
      if (line.lineType === 'Sticker' && line.unitCost) {
        out.donationAmount = line.unitCost * (line.quantity || 1);
      }
    } else if (line.donationCauseId) {
      out.donationCauseId = line.donationCauseId;

      // Single-cause campaign stickers need donationAmount for backend validation
      if (line.lineType === 'Sticker' && line.unitCost) {
        out.donationAmount = line.unitCost * (line.quantity || 1);
      }
    }

    if (line.donationAmount && !out.donationAmount) {
      out.donationAmount = line.donationAmount;
    }

    // V2 new fields from TICKET-1
    if (line.sweepstakesEntryCost) out.sweepstakesEntryCost = line.sweepstakesEntryCost;
    if (line.socialShopId) out.socialShopId = line.socialShopId;
    if (line.glowId) out.glowId = line.glowId;

    return out;
  });
}
